import { CommonModule, NgOptimizedImage } from '@angular/common';
import { booleanAttribute, Component, computed, inject, input, InputSignal, Signal, ChangeDetectionStrategy } from '@angular/core';
import { GsResponsiveDirective } from '../../directives/gs-responsive.directive';
import { ImageAccessibilityDirective } from '../../directives/image-accessibility.directive';
import { ImageSizesContext } from '../../directives/image-sizes.directive';
import { IPhotoEntityReference, MaybeResponsive, PhotoSize } from '../../models';
import { ImageService } from '../../services';

/** Render mode of the optimized image component. */
export type GsOptimizedImageMode = 'square' | 'natural' | 'fill' | 'adaptive';

/**
 * NgOptimizedImage wrapper that turns the backend's small/medium/large/original variants into
 * a responsive srcset. Four modes:
 * - `natural`: image dictates its own AR via [width]/[height] from widthFactor/heightFactor.
 * - `square`: wrapper enforces 1:1, img uses fill + object-cover.
 * - `fill`: wrapper sizing comes from the caller (containerClass/containerHeight), img uses fill.
 * - `adaptive`: wrapper takes `containerHeight` when set, otherwise the intrinsic AR — replaces
 *   the manual `<span [style.aspectRatio]>` pattern for designer-controlled heights.
 */
@Component({
  selector: 'gs-optimized-image',
  imports: [CommonModule, NgOptimizedImage, ImageAccessibilityDirective, GsResponsiveDirective],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './gs-optimized-image.component.html'
})
export class GsOptimizedImageComponent {
  // Services
  protected readonly imageService: ImageService = inject(ImageService);
  /** `sizes` announced by a surrounding container; absent outside one. */
  private readonly sizesContext: ImageSizesContext | null = inject(ImageSizesContext, { optional: true });

  // Inputs & Models
  /** The photo entity reference with all backend sizes. */
  photo: InputSignal<IPhotoEntityReference | undefined> = input<IPhotoEntityReference | undefined>(undefined);
  /** Which backend variant to use as the initial ngSrc URL. Accepts both the PhotoSize enum and the string literals 'small'|'medium'|'large'|'original' for template bindings. */
  size: InputSignal<`${PhotoSize}`> = input<`${PhotoSize}`>(PhotoSize.Medium);
  /** Render mode: natural (default), square, fill or adaptive. */
  mode: InputSignal<GsOptimizedImageMode> = input<GsOptimizedImageMode>('natural');
  /**
   * Browser hint for srcset selection (e.g. "100vw" or "(max-width: 768px) 100vw, 33vw"). Leave unset
   * to inherit the value of the surrounding `gsImageSizes` container, falling back to "100vw".
   */
  sizes: InputSignal<string> = input<string>('');
  /** Alternate text for the image (also used for accessibility). */
  alt: InputSignal<string> = input<string>('');
  /** Optional HTML title attribute on the <img> element (tooltip on hover). */
  title: InputSignal<string> = input<string>('');
  /** SEO description, applied as `data-seo-description`. */
  seoDescription: InputSignal<string> = input<string>('');
  /** Render the noPic fallback image instead of nothing when no photo is provided. */
  useNoPicFallback = input(false, { transform: booleanAttribute });
  /** Loading strategy. Ignored when priority=true. */
  loading: InputSignal<'lazy' | 'eager'> = input<'lazy' | 'eager'>('lazy');
  /** Priority loading for LCP-relevant images (hero, logo). Accepts the attribute-form `priority` (no value) as truthy. */
  priority = input(false, { transform: booleanAttribute });
  /** CSS classes applied to the <img> element. */
  imgClass: InputSignal<string> = input<string>('');
  /** CSS classes applied to the wrapper (only for mode=square|fill|adaptive). */
  containerClass: InputSignal<string> = input<string>('');
  /**
   * Optional container height in pixels (only for mode=fill|adaptive), fixed or configured per
   * breakpoint. Takes the raw configuration; a responsive value is resolved by CSS.
   */
  containerHeight: InputSignal<MaybeResponsive<number> | undefined> = input<MaybeResponsive<number> | undefined>(undefined);
  /** object-fit class for the <img> (e.g. "object-cover", "object-contain object-top"). */
  objectFit: InputSignal<string> = input<string>('');

  // Properties & Signals
  /** Endpoint path without the media base URL, falling back to noPic when enabled. */
  protected endpoint: Signal<string | undefined> = computed(() => {
    const photo = this.photo();
    if (photo) return this.imageService.getEndpoint(photo, this.size());
    if (this.useNoPicFallback()) return this.imageService.getNoPicEndpoint(this.size());
    return undefined;
  });
  /** Intrinsic width of the chosen backend variant in pixels. */
  protected intrinsicWidth: Signal<number> = computed(() => this.imageService.getIntrinsicWidth(this.photo(), this.size()));
  /** Intrinsic height of the chosen backend variant in pixels. */
  protected intrinsicHeight: Signal<number> = computed(() => this.imageService.getIntrinsicHeight(this.photo(), this.size()));
  /** loaderParams for NgOptimizedImage; undefined when no photo is present. */
  protected loaderParams: Signal<{ photo: IPhotoEntityReference } | undefined> = computed(() =>
    this.imageService.getLoaderParams(this.photo())
  );
  /** The `sizes` input, falling back to the surrounding container's value and finally to the full viewport. */
  protected effectiveSizes: Signal<string> = computed(() => this.sizes() || this.sizesContext?.sizes() || '100vw');
  /** srcset descriptors; carries the 2000w candidate only when the photo has an original. */
  protected srcset: Signal<string> = computed(() => this.imageService.srcsetFor(this.photo()));
  /** Combined img classes including objectFit. */
  protected computedImgClass: Signal<string> = computed(() => {
    const parts = [this.imgClass(), this.objectFit()].filter((p) => p && p.length > 0);
    return parts.join(' ');
  });
  /**
   * Built-in `size-full` for the fill-mode wrapper, dropped when the caller's
   * containerClass already fully sizes the wrapper itself — either via the `size-*`
   * shorthand or via both a `w-*` and an `h-*` utility. `size-full` and `size-12`
   * are both shorthand width/height rules with equal specificity, and `size-full`
   * is emitted later in the compiled CSS, so a caller's `size-12` would otherwise
   * lose the cascade and the absolutely-positioned image would collapse to 0. A
   * caller that supplies only one dimension (e.g. `w-full` + `containerHeight`)
   * still relies on `size-full` for the other and therefore keeps it.
   */
  protected fillSizeClass: Signal<string> = computed(() => {
    const cc = this.containerClass();
    const ownsSize = /(?:^|\s)size-/.test(cc) || (/(?:^|\s)w-/.test(cc) && /(?:^|\s)h-/.test(cc));
    return ownsSize ? '' : 'size-full';
  });
  /**
   * The photo's intrinsic aspect ratio, reserving the adaptive-mode wrapper wherever no height
   * applies. A configured height wins over it on the same element.
   */
  protected intrinsicAspectRatio: Signal<number> = computed(() => this.imageService.getIntrinsicAspectRatio(this.photo()));
  /**
   * Loading value forwarded to NgOptimizedImage. Undefined when the image is marked priority —
   * passing a loading value alongside priority triggers NG02952.
   */
  protected effectiveLoading: Signal<'lazy' | 'eager' | undefined> = computed(() => (this.priority() ? undefined : this.loading()));
}
